import type { AppSelectOption, SelectPaginatedData } from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { AppSelectPaginatedFetcherParams } from '@shared/ui/selects'

import { useTutorsListApi } from '@entities/tutor'
import { useAppSelectPaginated } from '@shared/ui/selects'

interface LessonActiveTutorSelectOptions {
  /** 0 is "-- Not selected --": the lessons lose their tutor. */
  tutorId: Ref<number>
}

interface LessonActiveTutorSelectReturn {
  refTutorId: WritableComputedRef<number | undefined>
  refTutorOptions: ComputedRef<AppSelectOption<number>[]>
  refSelectedOption: ComputedRef<AppSelectOption<number> | undefined>
  refHasMore: ComputedRef<boolean>
  refIsLoading: Readonly<Ref<boolean>>
  onSearch: (query: string) => void
  onLoadMore: (query: string) => void
  onOpen: () => void
}

export const LESSON_NO_TUTOR_ID = 0

const NO_TUTOR_OPTION: AppSelectOption<number> = { value: LESSON_NO_TUTOR_ID, text: '-- Not selected --' }

/** A searchable picker over every active tutor, as the legacy bulk edit offered, with "no tutor" on top. The chosen
 *  tutor is sent as `preselected`, so its label resolves even off the first page. */
export const useLessonActiveTutorSelect = ({ tutorId }: LessonActiveTutorSelectOptions): LessonActiveTutorSelectReturn => {
  const selectedCache = ref<AppSelectOption<number> | undefined>(undefined)

  const refTutorId = computed<number | undefined>({
    get: () => tutorId.value,
    set: (value) => {
      tutorId.value = value ?? LESSON_NO_TUTOR_ID
    },
  })

  const fetcher = ({ query, page, signal }: AppSelectPaginatedFetcherParams): Promise<SelectPaginatedData | null> =>
    useTutorsListApi({
      query,
      page,
      signal,
      active: true,
      preselected: tutorId.value ? [tutorId.value] : undefined,
    })

  const {
    options,
    hasMore: refHasMore,
    loading: refIsLoading,
    onSearch,
    onLoadMore,
    onOpen,
  } = useAppSelectPaginated(fetcher)

  const refTutorOptions = computed<AppSelectOption<number>[]>(() =>
    [NO_TUTOR_OPTION, ...(options.value as AppSelectOption<number>[])])

  const refSelectedOption = computed<AppSelectOption<number> | undefined>(() =>
    refTutorOptions.value.find(option => option.value === tutorId.value) ?? selectedCache.value)

  watch(refSelectedOption, (option) => {
    if (option) selectedCache.value = option
  })

  return {
    refTutorId,
    refTutorOptions,
    refSelectedOption,
    refHasMore,
    refIsLoading,
    onSearch,
    onLoadMore,
    onOpen,
  }
}
